import React from 'react';

import {
  Bell,
  Mail,
  Bot,
  PenSquare,
  Briefcase,
  CheckCircle2,
  CalendarDays,
  FolderOpen,
  BookOpen,
  FileText,
  Bookmark,
  Settings as SettingsIcon,
  TrendingUp,
  Sparkles,
  LayoutDashboard,
  Brain,
  Network,
  Star,
  Presentation,
  TableProperties,
  Cpu,
  BookMarked,
  ChefHat,
} from 'lucide-react';

import tasksV5Icon from '@/assets/icons/tasks-v5.png';

import { FEATURES } from '@/config/featureFlags';

import type { SidebarItem } from '@/components/v2/types';
import ideasKitchenLogo from '../../assets/ideas-kitchen-logo.png';

export const SIDEBAR_W = 280;
export const SIDEBAR_COLLAPSED_W = 72;

export const DEFAULT_SIDEBAR: SidebarItem[] = [
  { label: 'Dashboard', icon: <TrendingUp className="h-4 w-4" />, path: '/dashboard' },
  { label: 'Notifications', icon: <Bell className="h-4 w-4" />, path: '/notifications' },
  { label: 'Email', icon: <Mail className="h-4 w-4" />, path: '/email' },
  { label: 'Email V2 (Live)', icon: <Mail className="h-4 w-4" />, path: '/email-v2' },
  { label: 'Omniscient Agent', icon: <Star className="h-4 w-4" />, path: '/omniscient-agent' },
  { label: 'AI Assistant', icon: <Bot className="h-4 w-4" />, path: '/ai' },
  {
    label: 'AI Assistant v2 (Preview)',
    icon: (
      <svg viewBox="0 0 24 24" className="h-4 w-4">
        <path d="M4 4h16v12H5.17L4 17.17V4z" fill="currentColor" />
      </svg>
    ),
    path: '/ai-v2',
  },
  { label: 'Whiteboard', icon: <PenSquare className="h-4 w-4" />, path: '/whiteboard' },
  {
    label: 'Whiteboard V2 (Preview)',
    icon: <LayoutDashboard className="h-4 w-4" />,
    path: '/whiteboard-v2',
  },
  { label: 'GameTheoryV2 (Preview)', icon: <Brain className="h-4 w-4" />, path: '/game-theory-v2' },
  { label: 'MindMapping Smart', icon: <Network className="h-4 w-4" />, path: '/mindmap' },
  { label: 'FastLearn', icon: <TrendingUp className="h-4 w-4" />, path: '/fastlearn' },
  ...(FEATURES.SHOW_FASTLEARN_V2
    ? [
        {
          label: 'FastLearn v2 (Preview)',
          icon: <Sparkles className="h-4 w-4" />,
          path: '/fastlearn-v2',
        },
        {
          label: 'Presentations Hub',
          icon: <Presentation className="h-4 w-4" />,
          path: '/presentations-hub',
        },
      ]
    : []),
  { label: 'NotebookLM', icon: <BookMarked className="h-4 w-4" />, path: '/notebook-lm-v2' },
  { label: 'Smart Kitchen', icon: <ChefHat className="h-4 w-4" />, path: '/smart-kitchen' },
  {
    label: 'Ideas Kitchen',
    icon: (
      <img
        src={ideasKitchenLogo}
        alt=""
        aria-hidden="true"
        className="h-4 w-4 object-contain"
        draggable={false}
      />
    ),
    path: '/ideas-kitchen',
  },
  ...(FEATURES.SMART_NOTEBOOK_UI_ENABLED
    ? [{ label: 'Smart Notebook', icon: <Brain className="h-4 w-4" />, path: '/smart-notebook' }]
    : []),
  {
    label: 'Intelligence Studio',
    icon: <Sparkles className="h-4 w-4" />,
    path: '/intelligence-studio',
  },
  // WORK: Clients → Cases → Contracts → Documents → Tasks → Calendar
  { label: 'Clients', icon: <Briefcase className="h-4 w-4" />, path: '/clients' },
  { label: 'Cases', icon: <Briefcase className="h-4 w-4" />, path: '/cases' },
  { label: 'Contracts', icon: <FileText className="h-4 w-4" />, path: '/contracts' },
  {
    label: 'Contracts Analysis V1',
    icon: <FileText className="h-4 w-4" />,
    path: '/contracts-analysis-v1',
  },
  ...(FEATURES.CONTRACTS_V2_ENABLED
    ? [
        {
          label: 'Contracts Analysis V2',
          icon: <Brain className="h-4 w-4" />,
          path: '/contracts-analysis-v2',
        },
      ]
    : []),
  { label: 'Documents', icon: <FolderOpen className="h-4 w-4" />, path: '/documents' },
  {
    label: 'Excel Intelligence',
    icon: <TableProperties className="h-4 w-4" />,
    path: '/excel-intelligence',
  },
  {
    label: 'Excel Intelligence V2',
    icon: <Sparkles className="h-4 w-4" />,
    path: '/excel-intelligence-v2',
  },
  {
    label: 'Excel Intelligence V3',
    icon: <Cpu className="h-4 w-4" />,
    path: '/excel-intelligence-v3',
  },
  { label: 'Tasks', icon: <CheckCircle2 className="h-4 w-4" />, path: '/tasks' },
  { label: 'Tasks V2 (Preview)', icon: <CheckCircle2 className="h-4 w-4" />, path: '/tasks-v2' },
  { label: 'Tasks V3 Enhanced', icon: <Brain className="h-4 w-4" />, path: '/tasks-v3-enhanced' },
  { label: 'Tasks V4 (Preview)', icon: <Brain className="h-4 w-4" />, path: '/tasks-v4' },
  {
    label: 'Tasks V5',
    icon: (
      <img src={tasksV5Icon} alt="Tasks V5" className="h-4 w-4 object-contain" loading="lazy" />
    ),
    path: '/tasks-v5',
  },
  { label: 'Calendar', icon: <CalendarDays className="h-4 w-4" />, path: '/calendar' },
  // RESOURCES: Legal Research → Library → Forms
  { label: 'Legal Research', icon: <BookOpen className="h-4 w-4" />, path: '/legal-research' },
  {
    label: 'Legal Research V2',
    icon: <BookOpen className="h-4 w-4" />,
    path: '/legal-research-v2',
  },
  { label: 'Library', icon: <Bookmark className="h-4 w-4" />, path: '/library' },
  { label: 'Forms', icon: <FileText className="h-4 w-4" />, path: '/forms' },
  { label: 'Settings', icon: <SettingsIcon className="h-4 w-4" />, path: '/settings' },
];
